import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  AllRequestQueryResponseModel,
  RequestModel,
  RequestResponseModel,
} from "../../domain/models/requestModel";
import {
  GraphQLClient,
  GraphQLErrorMessage,
} from "../../../../shared/gql-client";
import { GET_ALL_STATES, GET_REQUEST } from "../queries/requestQueries";
import { toast } from "sonner";
import i18next from "i18next";
import { GraphQLReadResponse } from "../../../general/domain/models/graphql";
import RequestService from "../service/requestService";
import { RequestMutateSliceModel } from "./requestMutateSliceModel";
import LicensePlate from "license-plate";

const requestAPI = new RequestService();

export const isRequestFinished = (requestState: string) => {
  return ["refused", "cancelled", "delivered", "in_storage"].includes(
    requestState,
  );
};

export const getRequest = createAsyncThunk(
  "request/mutate/get",
  async (id: string, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      const response = (await client.query("requests", GET_REQUEST, {
        id: parseInt(id),
      })) as GraphQLReadResponse;

      const data = response.data[0] as RequestResponseModel;

      if (data.source?.car?.licence) {
        const licencePlate = new LicensePlate(data.source?.car?.licence);

        if (data.source?.car) {
          data.source.car.licence = `${licencePlate?.pretty()}`;
        }
      }

      return data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const createRequest = createAsyncThunk(
  "request/mutate/create",
  async (request: RequestModel, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    requestAPI.setClient(client);
    try {
      return await requestAPI.mutate(request);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const updateRequest = createAsyncThunk(
  "request/mutate/update",
  async (request: RequestModel, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    requestAPI.setClient(client);
    try {
      return await requestAPI.mutate(request);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const updateRequestState = createAsyncThunk(
  "request/state/update",
  async (
    { id, state, comment }: { id: number; state: string; comment?: string },
    thunkAPI,
  ) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    requestAPI.setClient(client);
    try {
      return await requestAPI.updateState(id, state, comment);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const deleteRequest = createAsyncThunk(
  "request/mutate/delete",
  async (id: number, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    requestAPI.setClient(client);
    try {
      return await requestAPI.delete(id);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const getAllRequestStates = createAsyncThunk(
  "request/mutate/AllRequestStates",
  async (_, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      return (await client.query(
        "allRequestStates",
        GET_ALL_STATES,
      )) as AllRequestQueryResponseModel;
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

const initialState = {
  loading: true,
  request: undefined,
} as RequestMutateSliceModel;

const requestMutateSlice = createSlice({
  name: "requestMutate",
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setLatestRequest: (state, action) => {
      if (state.request) {
        state.request = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRequest.fulfilled, (state, action) => {
      state.loading = false;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      state.request = action.payload;
    });
    builder.addCase(createRequest.fulfilled, (state, action) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      state.request = action.payload;
      toast.success(
        i18next?.t("common:request.created", {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          type: action.payload.type,
        }),
      );
    });
    builder.addCase(updateRequest.fulfilled, (state, action) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      state.request = action.payload;
      toast.success(
        i18next?.t("common:request.updated", {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          type: action.payload.type,
        }),
      );
    });
    builder.addCase(deleteRequest.fulfilled, (_state, action) => {
      toast.error(
        i18next?.t("common:request.deleted", {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          type: action.payload.type,
        }),
      );
    });
    builder.addMatcher(
      (action) =>
        [
          getRequest.rejected.type,
          createRequest.rejected.type,
          updateRequest.rejected.type,
        ].includes(action.type),
      (state, action: { payload: GraphQLErrorMessage }) => {
        state.loading = false;
        console.error(action.payload);
        toast.error(action.payload.message);
      },
    );
  },
});

export const { clear, setLoading, setLatestRequest } =
  requestMutateSlice.actions;

export default requestMutateSlice.reducer;
